
import { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import get from 'lodash/get'
import { byCompleteAndDate, findShipments, selectShipments } from '../../../../../root/reducers/shipments'
import { hasFeature } from '../../../../../van-shared/utils/features'
import capitalize from '../../../../../common/utils/capitalize'

/**
 * Hook for holding logic to fetch shipments (Currently used in InboundShipments and StockTransfers components ).
 *
 * @param {Object} params
 * @param {Object} params.api - FsApi.
 * @param {Object} params.user - Current user.
 * @param {Object} params.config - Config (applied with withConfigHoc).
 * @param {'complete' | 'incomplete' | 'all'} params.key - Key from shipments api
 * @param {(arg: Object) => boolean} [params.filters] - Optional filter for shipments.
 * @param {Object} params.options - Fetch shipment options
 *
 * @returns
 *  *   shipments: Object[],
 *  *   fullLocationName: string,
 *  *   loading: boolean,
 *  *   error: (Error|null)
 *  * }
 */
export default function useShipments ({ api, user, config, key, filters = () => true, options = {noExternalShipments: false} }) {
  const dispatch = useDispatch()
  const shipmentsFromStore = useSelector(selectShipments)

  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [locationObject, setLocationObject] = useState(null)

  const shipmentList = useMemo(
    () => byCompleteAndDate(shipmentsFromStore, user),
    [shipmentsFromStore, user]
  )

  const userLocation = user.location
  const withListInGeoLocation = hasFeature(config.features, 'shipments:listInGeoLocation')
  const useRouteSpecificFilter = true

  useEffect(() => {
    let mounted = true
    const initialize = async () => {
      setLoading(true)
      setError(null)
      try {
        await dispatch(
          findShipments(
            userLocation,
            withListInGeoLocation,
            useRouteSpecificFilter,
            options
          )
        )
        const loc = await api.location.get(userLocation.id)
        if (mounted) setLocationObject(loc)
      } catch (e) {
        if (mounted) setError(e)
      } finally {
        if (mounted) setLoading(false)
      }
    }
    initialize()
    return () => { mounted = false }
  }, [])

  const shipments = useMemo(() => {
    const filteredShipments = key === 'all'
      ? [...get(shipmentList, 'complete', []), ...get(shipmentList, 'incomplete', [])]
      : get(shipmentList, key, [])
    return filteredShipments.filter(filters)
  }, [shipmentList, filters])

  const locationNameFallback = useMemo(
    () => capitalize(userLocation.id.split(':').pop()),
    [userLocation.id]
  )
  const locationName = get(locationObject, 'fullName', locationNameFallback)

  return { shipments, locationName, loading, error }
}
